import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import { linkOptions } from '@tanstack/react-router';
import { useUserPermissions, useUserRoles } from '@anytag/auth';
import { TableCell } from '@anytag/shared-atoms';
import { SpreadButton } from '@anytag/shared-molecules';
import { getUserRoleString } from '@anytag/shared-utils';
import { AccountUserType } from '../types';

interface Props {
  onDelete: (account: { id: number; name: string }) => void;
}

export const useAccountUsersTable = ({ onDelete }: Props) => {
  const { t } = useTranslation();
  const { isAdmin, isStaff } = useUserRoles();
  const { hideEditUserBtn, hideDeleteUserBtn } = useUserPermissions();

  const columns: ColumnDef<AccountUserType>[] = [
    {
      accessorKey: 'name',
      header: t<string>('HeaderColumn.Name'),
      cell: ({ row }) => {
        const editUserLink = linkOptions({ to: '/user/$id', params: { id: row.original.id.toString() } });

        return (
          <div className="account-name-column">
            <TableCell.Link {...editUserLink} label={row.original.name} disabled={hideEditUserBtn} />
            <SpreadButton
              collapseFrom={0}
              moreButtonProps={{ disabled: hideDeleteUserBtn && hideEditUserBtn }}
              options={[
                {
                  label: t('Edit'),
                  icon: 'edit',
                  ...editUserLink,
                  hidden: hideEditUserBtn,
                  tooltip: { help: t('Button.Edit') }
                },
                {
                  icon: 'delete',
                  label: t('Delete'),
                  onClick: () => onDelete({ id: row.original.id, name: row.original.name }),
                  hidden: !(isAdmin && !hideDeleteUserBtn),
                  tooltip: { help: t('Button.Delete') }
                }
              ]}
            />
          </div>
        );
      }
    },
    {
      accessorKey: 'role',
      header: t<string>('HeaderColumn.Role'),
      cell: ({ row }) => <TableCell.Text value={t(`Option.${getUserRoleString(row.original.role)}`)} />
    },
    {
      accessorKey: 'companyName',
      meta: { hidden: isStaff },
      header: t<string>('HeaderColumn.CompanyName'),
      cell: ({ row }) => <TableCell.Text value={row.original.companyName || '-'} lines={2} />
    }
  ];

  return { columns };
};
